import React, { createContext, useContext, useState, useEffect } from 'react';
import { authAPI, userAPI } from './services/api';

const AuthContext = createContext();

// "Remember me" controls where the session lives: localStorage persists across
// browser restarts, sessionStorage clears when the tab/browser closes.
const persist = (token, user, rememberMe) => {
  const store = rememberMe ? localStorage : sessionStorage;
  const other = rememberMe ? sessionStorage : localStorage;
  store.setItem('token', token);
  store.setItem('user', JSON.stringify(user));
  other.removeItem('token');
  other.removeItem('user');
};

const clearSession = () => {
  localStorage.removeItem('token');
  localStorage.removeItem('user');
  sessionStorage.removeItem('token');
  sessionStorage.removeItem('user');
};

// Drop the path at a session boundary, so the next sign-in starts from the person's
// own landing page rather than wherever the last session happened to end.
//
// Dashboard unmounts on sign-out and remounts on sign-in, re-reading the URL as it
// goes. A leftover path reads as a deliberate deep link — which is right when
// following a link, and wrong straight after logging in: signing out on /team and
// back in as a non-admin landed on Access Denied, having asked for nothing.
//
// Also means the address bar stops advertising the last page viewed while the login
// screen is up, which matters on a shared machine.
const resetPath = () => {
  try {
    if (typeof window !== 'undefined' && window.location.pathname !== '/') {
      window.history.replaceState({}, '', '/');
    }
  } catch {
    // Non-fatal: worst case the landing preference is skipped once.
  }
};

// Read the current session's user from whichever store holds it.
const readStoredSession = () => {
  const token = localStorage.getItem('token') || sessionStorage.getItem('token');
  const userData = localStorage.getItem('user') || sessionStorage.getItem('user');
  return { token, userData };
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  // True only when a session died under us, so the login screen can say so rather
  // than looking like an ordinary sign-out the person chose.
  const [sessionExpired, setSessionExpired] = useState(false);

  useEffect(() => {
    const { token, userData } = readStoredSession();

    if (token && userData) {
      try {
        const parsedUser = JSON.parse(userData);
        setUser(parsedUser);
      } catch (error) {
        console.error('Error parsing user data:', error);
        clearSession();
      }
    }
    setLoading(false);
  }, []);

  // The api client raises this the first time a call comes back 401 with a token
  // that the server no longer accepts. Dropping the user here is what turns a
  // screen of empty lists into a login prompt that says what happened.
  useEffect(() => {
    const onExpired = () => {
      clearSession();
      // An expiry is a session boundary too, so the same reset applies.
      resetPath();
      setUser(null);
      setSessionExpired(true);
    };
    window.addEventListener('auth:expired', onExpired);
    return () => window.removeEventListener('auth:expired', onExpired);
  }, []);

  const login = async (email, password, rememberMe = true) => {
    try {
      const response = await authAPI.login({ email, password });
      const { token, user } = response.data;

      persist(token, user, rememberMe);
      // Before setUser, which is what mounts Dashboard and has it read the URL.
      // Clearing first is what lets the landing preference win on every sign-in.
      resetPath();
      setUser(user);
      setSessionExpired(false);

      return { success: true };
    } catch (error) {
        console.error('Login error:', error);
        return {
            success: false,
            error: error.response?.data?.title ||
                error.response?.data?.message ||
                error.response?.data ||
                'Invalid email or password'
        };
    }
  };

  // No register here any more. Creating an account is an admin action on someone
  // else's behalf — see userAPI.create, used by the Team page.
  //
  // This function persisted whatever the endpoint returned, which was right for
  // self-signup and wrong the moment an admin did it: the admin would have been
  // signed in as the user they just created. The endpoint no longer returns a token
  // either, so there is nothing left here to persist.

  const forgotPassword = async (email) => {
    try {
      const response = await authAPI.forgotPassword(email);
      return { success: true, ...response.data };
    } catch (error) {
      console.error('Forgot password error:', error);
      const data = error.response?.data;
      const message = typeof data === 'string' ? data
        : data?.title || data?.message || 'Failed to process request';
      return { success: false, error: message };
    }
  };

  const resetPassword = async (email, token, newPassword) => {
    try {
      await authAPI.resetPassword({ email, token, newPassword });
      return { success: true };
    } catch (error) {
      console.error('Reset password error:', error);
      const data = error.response?.data;
      const message = typeof data === 'string' ? data
        : data?.title || data?.message || 'Failed to reset password';
      return { success: false, error: message };
    }
  };

  const logout = () => {
    clearSession();
    resetPath();
    setUser(null);
  };

  const updateProfile = async ({ username, email, firstName, lastName, jobTitle, phone, photoUrl }) => {
    try {
      const res = await userAPI.updateMyProfile({ username, email, firstName, lastName, jobTitle, phone, photoUrl });
      const updated = res.data;
      // Merge into current user (preserves role, anything else not returned).
      const merged = { ...user, ...updated };
      // Update the user in whichever store currently holds the session.
      const store = localStorage.getItem('token') ? localStorage : sessionStorage;
      store.setItem('user', JSON.stringify(merged));
      setUser(merged);
      return { success: true };
    } catch (error) {
      const data = error.response?.data;
      const message = typeof data === 'string' ? data
        : data?.message || data?.title || 'Failed to update profile';
      return { success: false, error: message };
    }
  };

  // Helper functions for role checking
  const isAdmin = () => {
    return user?.role?.toLowerCase() === 'admin';
  };

  const isUser = () => {
    return user?.role?.toLowerCase() === 'user';
  };

  const hasRole = (role) => {
    return user?.role?.toLowerCase() === role.toLowerCase();
  };

  const value = {
    user,
    loading,
    sessionExpired,
    login,
    logout,
    updateProfile,
    forgotPassword,
    resetPassword,
    isAdmin,
    isUser,
    hasRole
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};